import React, { useEffect, useState, useCallback, memo } from 'react';
import { FiCamera, FiCheck, FiRefreshCw, FiX } from 'react-icons/fi';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './SuperAdminCandidates.module.css';
import AdminPagination from './AdminPagination';

const ROLES = [
  { value: 'RSM', label: 'Regional Sales Manager (RSM)' },
  { value: 'FSM', label: 'Field Sales Manager (FSM)' },
  { value: 'TSE', label: 'Territory Sales Manager (TSE)' },
];

const CandidateRowMemo = memo(function CandidateRow({ 
  c, 
  serialNumber,
  onToggleCamera, 
  cameraTogglingId, 
  getStatusClass, 
  getStatusLabel, 
  styles 
}) {
  const createdDate = new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const isTogglingCamera = cameraTogglingId === c._id;
  
  return (
    <tr key={c._id}>
      <td>{serialNumber}</td>
      <td>
        <div className={styles.candidateCell}>
          <div>
            <div className={styles.candidateName}>{c.fullName}</div>
            <div className={styles.candidateEmail}>{c.email}</div>
          </div>
        </div>
      </td>
      <td><span className={styles.roleTag}>{c.role}</span></td>
      <td><span className={`${styles.statusTag} ${getStatusClass(c.status)}`}>{getStatusLabel(c.status)}</span></td>
      <td className={styles.muted}>{createdDate}</td>
      <td>
        <button 
          className={`${styles.cameraToggleBtn} ${c.requireCamera ? styles.required : styles.notRequired}`}
          onClick={() => onToggleCamera(c._id)}
          disabled={isTogglingCamera}
          title={c.requireCamera ? 'Camera required' : 'Camera not required'}
        >
          {isTogglingCamera ? <FiRefreshCw aria-hidden="true" /> : (c.requireCamera ? <FiCamera aria-hidden="true" /> : <FiX aria-hidden="true" />)}
        </button>
      </td>
    </tr>
  );
});

export default function SuperAdminCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cameraTogglingId, setCameraTogglingId] = useState(null);
  const [cameraToggleSuccess, setCameraToggleSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, in_progress: 0, completed: 0 });
  const [cameraFilter, setCameraFilter] = useState('all');
  const { refreshKey } = useNavRefresh();

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCamera = cameraFilter === 'all' || 
                          (cameraFilter === 'required' ? c.requireCamera : !c.requireCamera);
    return matchesSearch && matchesCamera;
  });
  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedCandidates = filteredCandidates.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter === 'all' ? '' : `&status=${statusFilter}`;
      const res = await API.get(`/admin/candidates?${statusParam}`);
      if (Array.isArray(res.data)) {
        setCandidates(res.data);
      } else {
        setCandidates(res.data.data || []);
        if (res.data.counts) {
          setStatusCounts(res.data.counts);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleToggleCamera = async (candidateId) => {
    setCameraTogglingId(candidateId);
    try {
      const res = await API.patch(`/admin/candidates/${candidateId}/toggle-camera`);
      
      // Update the candidate in local state
      setCandidates(prev => prev.map(c => 
        c._id === candidateId 
          ? { ...c, requireCamera: !c.requireCamera }
          : c
      ));
      
      setCameraToggleSuccess(true);
      setTimeout(() => setCameraToggleSuccess(false), 3000);
    } catch (e) {
      console.error('Failed to toggle camera:', e);
      alert('Failed to update camera requirement');
    } finally {
      setCameraTogglingId(null);
    }
  };

  const getStatusClass = (status) => {
    const classMap = { pending: styles.pending, in_progress: styles.inProgress, completed: styles.completed };
    return classMap[status] || '';
  };

  const getStatusLabel = (status) => {
    const labelMap = { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' };
    return labelMap[status] || status;
  };

  const getCameraCount = (required) => {
    return candidates.filter(c => c.requireCamera === required).length;
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Camera Requirements</h1>
            <p className={styles.pageSub}>Manage camera requirements for candidates</p>
          </div>
        </div>

        {cameraToggleSuccess && (
          <div className={styles.successBox}>
            <div className={styles.successContent}>
              <span className={styles.successIcon}><FiCheck aria-hidden="true" /></span>
              <span className={styles.successText}>Camera requirement updated successfully!</span>
            </div>
          </div>
        )}

        <div className={styles.searchBar}>
          <input 
            type="text" 
            className={styles.searchInput} 
            placeholder="Search by name or email..." 
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
          />
          <div className={styles.filtersInline}>
            {[
              { value: 'all', label: 'All', count: statusCounts.all },
              { value: 'pending', label: 'Pending', count: statusCounts.pending },
              { value: 'in_progress', label: 'In Progress', count: statusCounts.in_progress },
              { value: 'completed', label: 'Completed', count: statusCounts.completed }
            ].map(f => (
              <button
                key={f.value}
                className={`${styles.filterBtn} ${statusFilter === f.value ? styles.active : ''}`}
                onClick={() => { setStatusFilter(f.value); setPage(1); }}
              >
                {f.label}
                <span className={styles.filterCount}>{f.count}</span>
              </button>
            ))}
          </div>
          <div className={styles.filtersInline}>
            {[
              { value: 'all', label: 'All', count: candidates.length },
              { value: 'required', label: 'Camera Required', count: getCameraCount(true) },
              { value: 'notRequired', label: 'Camera Disabled', count: getCameraCount(false) }
            ].map(f => (
              <button
                key={f.value}
                className={`${styles.filterBtn} ${cameraFilter === f.value ? styles.active : ''}`}
                onClick={() => { setCameraFilter(f.value); setPage(1); }}
              >
                {f.label}
                <span className={styles.filterCount}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <p className={styles.loadingText}>Loading candidates...</p>
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className={styles.emptyContainer}>
            <p className={styles.emptyText}>No candidates found</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Candidate</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Camera</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCandidates.map((c, index) => (
                <CandidateRowMemo
                  key={c._id}
                  c={c}
                  serialNumber={(currentPage - 1) * pageSize + index + 1}
                  onToggleCamera={handleToggleCamera}
                  cameraTogglingId={cameraTogglingId}
                  getStatusClass={getStatusClass}
                  getStatusLabel={getStatusLabel}
                  styles={styles}
                />
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredCandidates.length > 0 && (
          <AdminPagination
            page={currentPage}
            pageSize={pageSize}
            total={filteredCandidates.length}
            onPageChange={setPage}
            onPageSizeChange={size => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
